import {  useMemo, useState } from 'react'
import { useHandleResize } from '../../../hooks/useHandleResize';
import { selectCurrentUserClientID, selectCurrentUserId } from '../../../features/auth/authSlice';
import { useSelector } from 'react-redux';
import { selectItemLoading } from '../../../features/guest/guestSlice';
import { useDynamicQueryData } from '../../../hooks/api/useDynamicQueryData';
import { useMutationData } from '../../../hooks/api/useMutationData';
import useGuestAction from '../../../hooks/actions/useGuestAction';
import { CartItem } from '../../../types/response';
import { isProductOrderable } from '../../../utils/isProductOrderable';
import Loading from '../../ui/Loading';
import ErrorMessage from '../../ui/ErrorMessage';
import MyCartItem from '../../common/items/MyCartItem';
import { SolarCart3Bold } from '../../../assets/icons/Icons';
import { useStoreTimer } from '../../../utils/useStoreTimer';
import { useOrderSocket } from '../../../hooks/socket/useOrderSocket';

interface CartModalProps {
    isStoreOpen: boolean;
}

export default function CartModal({ isStoreOpen }: CartModalProps) {

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const { storeOpen  } = useStoreTimer();
    const guestId = useSelector(selectCurrentUserId)
    const clientId = useSelector(selectCurrentUserClientID)
    const loadingItemId = useSelector(selectItemLoading)
    const { availableProductOfClient, guestCart, isErrorGuestCart, isLoadingGuestCart, errorMessageGuestCart, } = useDynamicQueryData({guestId, clientId})
    const { isLoadingRemoveItemCart, isLoadingCheckOutOrder, isLoadingUpdateCartQty, } = useMutationData()
    const guestCartItem: CartItem[] = guestCart?.items ?? []
    const isNotEmptyCart: boolean = guestCartItem.length !== 0
    const { handleUpdateQty, handleRemoveItemCart, handlePlaceOrderCart } = useGuestAction()
    useOrderSocket()

    // Available product IDs
    const availableProductIds = useMemo(() => {
        return new Set(
            (availableProductOfClient?.data ?? []).map(product => product.productID)
        );
    }, [availableProductOfClient?.data]);

    // Valid cart items
    const validCartItems = useMemo(() => {
        return guestCartItem.filter(cartItem =>
            availableProductIds.has(cartItem.productID)
        );
    }, [guestCartItem, availableProductIds]);

    // Whether there's at least one valid item
    const hasOrderableItems = useMemo(() => {
        return validCartItems.length > 0;
    }, [validCartItems]);

    const isDisabledPlacetOrderBtn = isLoadingCheckOutOrder || !isStoreOpen || !hasOrderableItems;

    useHandleResize({setWindowHeight})

    return (
        <div 
            className="absolute top-full right-0 mt-[18px] w-[350px] min-[425px]:min-w-[395px] md:w-[450px] bg-white h-[650px] shadow-lg rounded-lg flex flex-col overflow-hidden p-2.5 space-y-1 "
            style={{ maxHeight: `${windowHeight - 100}px`, overflowY: "auto", zIndex: '20'}}
        >   
            <div className='overflow-hidden flex flex-col space-y-1 h-full'>
                <div className="p-2 text-black flex justify-between items-center">
                    <p className='text-sm font-semibold flex items-center justify-center'>My Cart</p>
                </div>
                <hr className="h-px bg-gray-200 border-0" />
                <div className="flex flex-col  overflow-hidden rounded h-full">
                    <div className='flex-1 overflow-y-auto  p-2 space-y-3 max-h-none'>
                        {isLoadingGuestCart ? (
                            <Loading message="Loading" />
                        ) : isErrorGuestCart ? (
                            <div className='h-full w-full flex items-center justify-center'>
                                <ErrorMessage message={errorMessageGuestCart} />
                            </div>
                        ) : isNotEmptyCart ? (
                            guestCartItem.map((cart, i) => {
                                const isProductAvailable = availableProductOfClient?.data?.some((product) => product.productID === cart.productID);
                                const isActive = cart.isActive;
                                const isAvailable = cart.isAvailable;
                                const firstWord = cart.categoryName?.split(' ')[0] || "";
                                const isDisabledRemoveItem = !isStoreOpen || isLoadingCheckOutOrder
                                const isDisabledQty = !isProductAvailable || isLoadingUpdateCartQty || isLoadingCheckOutOrder || loadingItemId === cart.productID;
                                return (
                                    <MyCartItem
                                        isActive={isActive}
                                        isAvailable={isAvailable}
                                        storeOpen={storeOpen}
                                        key={cart.cartItemID}
                                        cart={cart}
                                        isProductAvailable={isProductAvailable}
                                        firstWord={firstWord}
                                        isLoadingRemoveItemCart={isLoadingRemoveItemCart}
                                        handleUpdateQty={handleUpdateQty}
                                        handleRemoveItemCart={handleRemoveItemCart}
                                        isDisabledRemoveItem={isDisabledRemoveItem}
                                        isDisabledQty={isDisabledQty}
                                        loadingItemId={loadingItemId}
                                        isStoreOpen={isStoreOpen}
                                    />
                                );
                            })
                        ) : (
                            <div className="h-full w-full flex flex-col space-y-2 items-center justify-center">
                                <SolarCart3Bold color='#aa6833' width={70} height={70} />
                                <p className="text-base font-semibold">Your cart is empty.</p>
                            </div>
                        )}
                    </div>
                </div>
            
                {isNotEmptyCart && (
                    <div className=''>
                        <button 
                            onClick={handlePlaceOrderCart}
                            disabled={isDisabledPlacetOrderBtn}
                            className={`w-full rounded-b-lg py-2.5 text-sm font-medium text-white transition ${
                                isDisabledPlacetOrderBtn
                                    ? "cursor-not-allowed bg-gray-300"
                                    : "bg-cbColor"
                            }`}
                        >
                            {isLoadingCheckOutOrder ? (
                                <Loading setH={'h-[20px]'} message={'Placing Order...'}/>
                            ) : ('Place order')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
